import Lottie from "lottie-react";
import { Calculator } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dimmer, Grid } from "semantic-ui-react";
import {
  useGetIngredientPackaging,
  useGetIngredientsSupplierList,
  useGetIngredientWholeList,
  usePutIngredientDataToQueue,
  useUploadIngredientImage
} from "../../../api/ingredients";
import calculatorLoading from "../../../assets/animations/CalculatorLoading.json";
import { CommonModal, TitleView } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { IngredientDataType, IngredientType } from "../../../config/enums/IngredientType.enum";
import { MappingSourceType } from "../../../config/enums/MappingSourceType.enum";
import { errorView } from "../../../helpers/ErrorHandler";
import TransportationDetails from "../transportationDetails/TransportationDetails";
import IngredientDataRegister from "./IngredientDataRegister";
import IngredientPackagingView from "./IngredientPackageing";
import "./IngredientRegister.scss";
import SupplierIngredientLcaDataView, {
  SupplierIngredientLcaDataViewRef
} from "../shared/components/SupplierIngredientLcaDataView";
import SupplerRegister from "./SupplierRegister";

interface IngredientRegisterModalProps {
  state?: any;
  visibleModal: boolean;
  setVisibleModal: (value: boolean) => void;
  ingredientList?: any[];
  refetchIngredients?: () => void;
}

interface HandleValidation {
  resetSupplierData(): void;
  saveData(documents?: any[]): void;
  getSupplierData(): any;
}

const IngredientRegisterModalView = ({
  visibleModal,
  setVisibleModal,
  ingredientList,
  refetchIngredients
}: IngredientRegisterModalProps) => {
  const [ingredientData, setIngredientData] = useState<any>(undefined);
  const [showSupplier, setShowSupplier] = useState<boolean>(false);
  const [imageData, setImageData] = useState<File | null>(null);
  const [loadPackageData, setLoadPackageData] = useState<any>(false);
  const [supplierData, setSupplierData] = useState<any>(undefined);
  const [hideIngredientView, setHideIngredientView] = useState<boolean>(false);
  const [currentDataType, setCurrentDataType] = useState<any>("SUPPLIER");
  const [viewEnvironmentalData, setViewEnvironmentalData] = useState<boolean>(false);
  const [currentSupplierId, setCurrentSupplierId] = useState<any>(null);
  const [viewPackage, setViewPackage] = useState<any>({
    processIngredient: false,
    produceIngredient: false,
    enableIngredient: false,
    lcaIngredient: false
  });
  const [validateData, setValidateData] = useState({
    country: 1,
    specifyIngredientValue: "",
    produceType: "",
    ingredientType: ""
  });
  const [calculateModalOpen, setCalculateModalOpen] = useState<boolean>(false);
  const [supplierValidation, setSupplierValidation] = useState<any>(false);
  const [isSupplierFormOpen, setIsSupplierFormOpen] = useState<boolean>(false);
  const navigation = useNavigate();

  const disableCalculateButton = useMemo(
    () => !((isSupplierFormOpen && supplierValidation) || currentSupplierId),
    [currentSupplierId, supplierValidation, isSupplierFormOpen]
  );

  const ref = useRef<HandleValidation>();
  const lcaRef = useRef<SupplierIngredientLcaDataViewRef>(null);
  const packageDataView = viewPackage.enableIngredient ? true : false;
  const transportDataView = viewPackage.processIngredient ? true : false;
  const lcaDataView = viewPackage.lcaIngredient ? true : false;

  const calculationTile = "Are you sure you have entered all the data for ingredient calculation?";

  let ingredientId = useMemo(() => {
    return ingredientData?.ingredient_data?.ingredient_id || ingredientData?.ingredient_data?._id;
  }, [ingredientData]);

  let supplierId = useMemo(() => {
    return supplierData?.supplier_data?.supplier_id || supplierData?.supplier_data?._id;
  }, [supplierData]);

  let getPackagingData = {
    ingredientId: ingredientId,
    supplierId: supplierId,
    loadPackageData: loadPackageData
  };

  useEffect(() => {
    let getSupplierIngredientDetails = Array.isArray(
      supplierData?.supplier_data?.supplierIngredientDetails
    )
      ? supplierData.supplier_data.supplierIngredientDetails.find(
          (e: any) => e.ingredient_id == ingredientId
        )
      : supplierData?.supplier_data?.supplierIngredientDetails?.data_type;

    if (getSupplierIngredientDetails?.data_type) {
      setViewEnvironmentalData(true);
    }
  }, [supplierData]);

  useEffect(() => {
    if (!visibleModal) {
      setIngredientData(undefined);
      setSupplierData(undefined);
      setLoadPackageData(false);
      setHideIngredientView(false);
    }
  }, [visibleModal]);

  const { data: ingredientListByProduct, refetch: ingredientListByProductRefetch } =
    useGetIngredientWholeList(true);

  const { data: ingredientSupplierList, refetch: supplerListByProductRefetch } =
    useGetIngredientsSupplierList();

  const { data: ingredientPackageData, refetch: ingredientPackageDataRefetch } =
    useGetIngredientPackaging(getPackagingData);

  const { mutate: uploadIngredientImage } = useUploadIngredientImage();

  const handleCloseModal = () => {
    if (hideIngredientView) return;
    setVisibleModal(false);
  };
  const saveIngredientImage = (data: any) => {
    if (imageData) {
      const ingredientImageData = {
        ingredient_id: data?.ingredient_data?._id,
        file: imageData
      };
      uploadIngredientImage(ingredientImageData);
    }
  };

  const closeModal = () => {
    refetchIngredients?.();
    if (hideIngredientView) {
      ref?.current?.resetSupplierData();
    } else {
      handleCloseModal();
    }
  };

  const handleSave = async () => {
    if (!ref?.current) return;

    try {
      // Get uploaded documents
      const uploadedDocuments = lcaRef?.current?.getUploadedDocuments() || [];

      // Pass documents directly to saveData
      await ref.current.saveData(uploadedDocuments);

      // Close modal after successful save
      closeModal();
    } catch (error) {
      errorView("Save failed");
    }
  };

  const { mutate: putIngredientDataToQueue, isLoading: isLoadingPutDataToQueue } =
    usePutIngredientDataToQueue();

  const calculateHandleApprove = () => {
    putIngredientDataToQueue(
      { ingredientId, supplierId },
      {
        onSuccess: () => {
          setCalculateModalOpen(false);
          refetchIngredients?.();
          setVisibleModal(false);
        },
        onError: () => {
          setCalculateModalOpen(false);
        }
      }
    );
  };

  const enablePackageData = useMemo(() => {
    return loadPackageData && packageDataView && viewEnvironmentalData;
  }, [loadPackageData, packageDataView, viewEnvironmentalData]);

  const enableTransportData = useMemo(() => {
    const enableTransportDetails = loadPackageData && transportDataView && viewEnvironmentalData;
    return (
      enableTransportDetails ||
      (!enableTransportDetails && packageDataView && viewEnvironmentalData)
    );
  }, [loadPackageData, transportDataView, viewEnvironmentalData]);

  const enableLCAData = useMemo(() => {
    return lcaDataView && viewEnvironmentalData;
  }, [lcaDataView, viewEnvironmentalData]);

  const enableIngredientMapping = useMemo(
    () =>
      (currentDataType === IngredientDataType.USER ||
        currentDataType === IngredientDataType.GENERIC) &&
      ingredientId &&
      supplierId &&
      viewEnvironmentalData,
    [currentDataType, viewEnvironmentalData, validateData]
  );

  const isGenericCalculationReady = useMemo(() => {
    if (currentDataType !== IngredientDataType.GENERIC || !viewEnvironmentalData) return false;

    if (validateData.ingredientType === IngredientType.RAW) {
      return Boolean(validateData.country && validateData.produceType?.trim());
    }

    if (validateData.ingredientType === IngredientType.PROCESSED) {
      return Boolean(validateData.country && validateData.specifyIngredientValue?.trim());
    }

    return false;
  }, [currentDataType, validateData, viewEnvironmentalData]);

  const genericButtonTitle = isGenericCalculationReady ? "Save and Calculate" : "Save";

  const isIngredientMappingMode = currentDataType === IngredientDataType.USER;

  const handleGenericPrimaryAction = async () => {
    if (!ref?.current) return;

    try {
      // Get uploaded documents
      const uploadedDocuments = lcaRef?.current?.getUploadedDocuments() || [];

      // Pass documents directly to saveData
      await ref.current.saveData(uploadedDocuments);

      if (isGenericCalculationReady) {
        setCalculateModalOpen(true);
      }
    } catch {
      errorView("An error occurred while saving the ingredient data. Please try again.");
    }
  };

  const enableIngredientButton = useMemo(
    () =>
      supplierData?.supplier_data?.dataType
        ? !(
            supplierData?.supplier_data?.dataType === IngredientDataType.USER ||
            supplierData?.supplier_data?.dataType === IngredientDataType.GENERIC
          )
        : true,
    [ingredientData, supplierData]
  );

  const checkCurrentSupplier = (supplierId: any) => {
    setCurrentSupplierId(supplierId);
  };

  const navigateIngredientMapping = () =>
    navigation(
      `/mapping/${MappingSourceType.ingredient}?sourceId=${ingredientId}&ownerId=${supplierId}&ingredientName=${ingredientData?.ingredient_name}`
    );

  if (isLoadingPutDataToQueue) {
    return (
      <Dimmer active>
        <div className="loading-calculation">
          <Lottie animationData={calculatorLoading} loop={true} />
        </div>
      </Dimmer>
    );
  }

  return (
    <CommonModal
      modalOpenState={visibleModal}
      modalCloseState={() => closeModal()}
      centered={true}
      title="Add Ingredients"
    >
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <CommonSplitContainer
            currentHeight={0.91}
            topChild={
              <div>
                <TitleView
                  title={!hideIngredientView ? "General Details" : "Add New Supplier"}
                  CustomTitleViewMain="ingredientRegisterTitle"
                />
                <Grid className="mt-1">
                  {!hideIngredientView ? (
                    <Grid.Column
                      computer={16}
                      tablet={16}
                      mobile={16}
                      className="paddingRemoveBottom"
                    >
                      <IngredientDataRegister
                        ingredientListByProduct={ingredientListByProduct}
                        ingredientRegisterData={setIngredientData}
                        refetchIngredient={ingredientListByProductRefetch}
                        currentIngredientData={ingredientData}
                        showSupplier={setShowSupplier}
                        setViewPackage={setViewPackage}
                        setImageData={setImageData}
                        isRegister={true}
                      />
                    </Grid.Column>
                  ) : null}
                  <Grid.Column computer={16} tablet={16} mobile={16}>
                    <SupplerRegister
                      ref={ref}
                      lcaRef={lcaRef}
                      onChangeValidateData={setValidateData}
                      ingredientSupplierList={ingredientSupplierList}
                      ingredientData={ingredientData}
                      supplierCurrentData={() => {}}
                      ingredientList={ingredientList}
                      refetchSupplier={supplerListByProductRefetch}
                      setIngredientData={setIngredientData}
                      setSupplier={setSupplierData}
                      loadPackageData={setLoadPackageData}
                      hideIngredientView={(status: any) => setHideIngredientView(status)}
                      changeDataIngredient={setViewPackage}
                      disable={showSupplier}
                      setViewEnvironmentalData={setViewEnvironmentalData}
                      viewEnvironmentalData={viewEnvironmentalData}
                      saveIngredientImage={saveIngredientImage}
                      currentDataType={setCurrentDataType}
                      isEnableEnvironmentData={true}
                      checkCurrentSupplier={checkCurrentSupplier}
                      setSupplierValidation={setSupplierValidation}
                      setIsSupplierFormOpen={setIsSupplierFormOpen}
                    />
                  </Grid.Column>
                </Grid>
                {enablePackageData ? (
                  <IngredientPackagingView
                    ingredientId={ingredientId}
                    supplierId={supplierId}
                    refetchPackagingData={ingredientPackageDataRefetch}
                    ingredientPackageData={ingredientPackageData}
                  />
                ) : null}

                {enableTransportData ? (
                  <TransportationDetails ingredientId={ingredientId} supplierId={supplierId} />
                ) : null}

                {enableLCAData ? (
                  <>
                    <SupplierIngredientLcaDataView
                      ref={lcaRef}
                      ingredientId={ingredientId}
                      supplierId={supplierId}
                    />
                  </>
                ) : null}
                <ConfirmModal
                  viewModal={calculateModalOpen}
                  closeModal={() => {
                    setCalculateModalOpen(false);
                  }}
                  cancel={() => setCalculateModalOpen(false)}
                  approve={calculateHandleApprove}
                  title={"Calculation Ingredient Data"}
                  subTitle={calculationTile}
                  variant="default"
                  confirmText="Calculate"
                  icon={<Calculator className="tw-h-5 tw-w-5" />}
                />
              </div>
            }
            bottomChild={
              <MainBottomMultipleButtonView
                leftButtons={[
                  { title: "Cancel", onClick: () => closeModal(), buttonOutLine: true }
                ]}
                rightButtons={
                  isIngredientMappingMode
                    ? [
                        {
                          contentView: 10,
                          title: "Ingredient Mapping",
                          onClick: navigateIngredientMapping,
                          type: "submit",
                          buttonView: enableIngredientMapping,
                          disabled: enableIngredientButton
                        },
                        {
                          contentView: enableIngredientMapping ? 6 : 16,
                          title: "Save",
                          onClick: handleSave,
                          type: "submit",
                          buttonView: true,
                          disabled: disableCalculateButton
                        }
                      ]
                    : [
                        {
                          contentView: 16,
                          title: genericButtonTitle,
                          onClick: handleGenericPrimaryAction,
                          type: "submit",
                          buttonView: true,
                          disabled: disableCalculateButton
                        }
                      ]
                }
              />
            }
          />
        </Grid.Column>
      </Grid>
    </CommonModal>
  );
};

export default IngredientRegisterModalView;
